<template>
  <div class="cancel-subscription-page">
    <div class="cancel-container">
      <div class="cancel-header">
        <h1>❌ サブスクリプション解約</h1>
        <p class="subtitle">メール認証による安全な解約手続き</p>
      </div>

      <!-- ローディング状態 -->
      <div v-if="loading" class="loading-state">
        <p>⏳ 認証情報を確認しています...</p>
      </div>

      <!-- エラー状態 -->
      <div v-else-if="error" class="error-state">
        <h3>❌ エラーが発生しました</h3>
        <p>{{ error }}</p>
        <button class="btn btn-primary" @click="$router.push('/mypage')">
          マイページに戻る
        </button>
      </div>

      <!-- 解約フォーム -->
      <div v-else class="cancel-form-container">
        <div class="user-info">
          <h3>📋 解約対象のサブスクリプション</h3>
          <div class="info-card">
            <p v-if="learnerName"><strong>解約対象:</strong> {{ learnerName }} の契約</p>
            <p><strong>メールアドレス:</strong> {{ userEmail }}</p>
            <p><strong>現在のプラン:</strong> {{ planName }}</p>
            <p><strong>次回請求日:</strong> {{ formatDate(nextBillingDate || currentPeriodEnd) }}</p>
          </div>
        </div>

        <div class="cancel-warning">
          <h4>⚠️ 解約について</h4>
          <ul>
            <li><strong>サービス利用期限:</strong> {{ formatDate(nextBillingDate || currentPeriodEnd) }}まで</li>
            <li><strong>返金:</strong> 期末解約のため、返金はありません</li>
            <li><strong>データ保持:</strong> 解約後もデータは保持されますが、アクセスできません</li>
            <li><strong>再開:</strong> 解約後の再開は新規登録が必要です</li>
          </ul>
        </div>

        <form @submit.prevent="executeCancellation" class="cancel-form">
          <div class="form-group">
            <label for="cancel-reason">解約理由 <span class="optional">(任意)</span></label>
            <select id="cancel-reason" v-model="cancelForm.reason" class="form-control">
              <option value="">選択してください</option>
              <option value="cost">料金が高い</option>
              <option value="no_time">時間がない</option>
              <option value="not_suitable">サービスが合わない</option>
              <option value="technical_issues">技術的な問題</option>
              <option value="goal_achieved">目標を達成した</option>
              <option value="temporary_pause">一時的な休止</option>
              <option value="other">その他</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="cancel-feedback">ご意見・ご要望 <span class="optional">(任意)</span></label>
            <textarea 
              id="cancel-feedback" 
              v-model="cancelForm.feedback" 
              class="form-control"
              rows="4"
              placeholder="サービス改善のため、ご意見をお聞かせください（任意）"
            ></textarea>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" @click="$router.push('/mypage')">
              キャンセル
            </button>
            <button 
              type="submit" 
              class="btn btn-danger" 
              :disabled="cancelLoading"
            >
              {{ cancelLoading ? '解約処理中...' : '解約を実行' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '../supabaseClient'

const route = useRoute()
const router = useRouter()

// リアクティブデータ
const loading = ref(true)
const error = ref(null)
const cancelLoading = ref(false)
const userEmail = ref('')
const planName = ref('')
const nextBillingDate = ref('')
const currentPeriodEnd = ref('')
const learnerId = ref(null)
const learnerName = ref('')
const accessToken = ref('')

const cancelForm = ref({
  reason: '',
  feedback: ''
})

// 日付フォーマット
const formatDate = (dateString) => {
  if (!dateString) return '不明'
  try {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (e) {
    return '不明'
  }
}

// ハッシュから access_token / refresh_token をパース（トークンに = が含まれるため最初の = で分割）
function parseHashParams (hash) {
  if (!hash || !hash.startsWith('#')) return {}
  const params = {}
  hash.slice(1).split('&').forEach((part) => {
    const idx = part.indexOf('=')
    if (idx === -1) return
    const key = part.slice(0, idx)
    const value = part.slice(idx + 1)
    if (key && value) params[key] = decodeURIComponent(value)
  })
  return params
}

// 解約実行（Bearer で API 呼び出し）
const executeCancellation = async () => {
  try {
    const finalConfirm = confirm(
      `本当に解約しますか？\n\n` +
      `⚠️ 重要事項:\n` +
      `• 次回請求日（${formatDate(nextBillingDate.value || currentPeriodEnd.value)}）までサービスを利用できます\n` +
      `• 解約後の再開は新規登録が必要です\n` +
      `• データは削除されませんが、アクセスできなくなります`
    )
    if (!finalConfirm) return

    const token = accessToken.value
    if (!token) {
      alert('セッションが切れています。メールのリンクから再度アクセスしてください。')
      return
    }

    cancelLoading.value = true

    const body = {
      reason: cancelForm.value.reason,
      feedback: cancelForm.value.feedback
    }
    if (learnerId.value) {
      body.learner_id = learnerId.value
    }
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || '解約処理に失敗しました')
    }

    const data = await response.json()
    console.log('✅ 解約成功:', data)

    alert(
      `✅ 解約手続きが完了しました\n\n` +
      `📅 サービス利用期限: ${data.details.serviceEndDate}\n` +
      `💸 返金額: ¥${data.details.refundAmount?.toLocaleString() || '0'}\n\n` +
      `ご利用いただき、ありがとうございました。`
    )

    router.push('/')
  } catch (err) {
    console.error('❌ 解約エラー:', err)
    alert(`解約処理でエラーが発生しました: ${err.message}`)
  } finally {
    cancelLoading.value = false
  }
}

// ページ初期化: Supabase リダイレクトのハッシュからセッション設定 → 解約情報取得
onMounted(async () => {
  try {
    const hash = route.hash || window.location.hash
    const params = parseHashParams(hash)

    const token = params.access_token
    const refresh = params.refresh_token

    if (!token) {
      error.value = '解約用のリンクからアクセスしてください。メールに送ったリンクを開いてこのページに進んでください。'
      loading.value = false
      return
    }

    await supabase.auth.setSession({
      access_token: token,
      refresh_token: refresh || ''
    })

    // ハッシュを URL から削除（再読み込みでトークンが漏れないように）
    if (window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    accessToken.value = token

    const { data: { session } } = await supabase.auth.getSession()
    const bearer = session?.access_token
    if (!bearer) {
      error.value = 'セッションの取得に失敗しました。メールのリンクから再度アクセスしてください。'
      loading.value = false
      return
    }

    const learnerIdQuery = route.query.learner_id || route.query.learnerId || null
    if (learnerIdQuery) learnerId.value = learnerIdQuery
    const infoUrl = learnerId.value
      ? `${import.meta.env.VITE_API_BASE_URL}/api/subscription-info?learner_id=${encodeURIComponent(learnerId.value)}`
      : `${import.meta.env.VITE_API_BASE_URL}/api/subscription-info`
    const infoRes = await fetch(infoUrl, {
      headers: { 'Authorization': `Bearer ${bearer}` }
    })
    if (!infoRes.ok) {
      const errData = await infoRes.json()
      throw new Error(errData.error || 'サブスクリプション情報の取得に失敗しました')
    }
    const info = await infoRes.json()
    userEmail.value = info.email || '不明'
    planName.value = info.planName || 'プラン情報を取得できませんでした'
    currentPeriodEnd.value = info.currentPeriodEnd || null
    if (info.learnerName) learnerName.value = info.learnerName

    loading.value = false
  } catch (err) {
    console.error('初期化エラー:', err)
    error.value = err.message || 'ページの読み込みに失敗しました'
    loading.value = false
  }
})
</script>

<style scoped>
.cancel-subscription-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  overflow: hidden;
}

.cancel-header {
  background: #fef2f2;
  padding: 30px 20px;
  text-align: center;
  border-bottom: 1px solid #fecaca;
}

.cancel-header h1 {
  margin: 0 0 10px 0;
  color: #dc2626;
  font-size: 28px;
  font-weight: 600;
}

.subtitle {
  margin: 0;
  color: #7f1d1d;
  font-size: 14px;
}

.loading-state, .error-state {
  padding: 40px 20px;
  text-align: center;
}

.error-state h3 {
  color: #dc2626;
  margin-bottom: 16px;
}

.cancel-form-container {
  padding: 30px;
}

.user-info {
  margin-bottom: 30px;
}

.user-info h3 {
  margin: 0 0 16px 0;
  color: #374151;
  font-size: 18px;
}

.info-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.info-card p {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 14px;
}

.info-card p:last-child {
  margin-bottom: 0;
}

.cancel-warning {
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 30px;
}

.cancel-warning h4 {
  margin: 0 0 12px 0;
  color: #92400e;
  font-size: 16px;
  font-weight: 600;
}

.cancel-warning ul {
  margin: 0;
  padding-left: 20px;
  color: #92400e;
}

.cancel-warning li {
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
}

.optional {
  font-weight: normal;
  color: #6b7280;
  font-size: 12px;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 30px;
}

.btn {
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  text-decoration: none;
  min-width: 120px;
}

.btn-secondary {
  background: #f9fafb;
  color: #374151;
  border-color: #d1d5db;
}

.btn-secondary:hover {
  background: #f3f4f6;
}

.btn-danger {
  background: #dc2626;
  color: white;
  border-color: #dc2626;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn-danger:disabled {
  background: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* モバイル対応 */
@media (max-width: 768px) {
  .cancel-subscription-page {
    padding: 10px;
  }
  
  .cancel-container {
    max-width: 100%;
  }
  
  .cancel-form-container {
    padding: 20px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>
